import React, { useEffect, useState } from "react";
import { RefreshCw, Download, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LoadErrorBanner, { describeLoadError } from "@/components/admin/LoadErrorBanner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function DeletionAuditPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      const { data } = await api.get(`/admin/deletion-audit?${params.toString()}`);
      setRows(data.items);
      setTotal(data.total);
    } catch (e) {
      setRows([]); setTotal(0);
      setLoadError(describeLoadError(e, "deletion audit"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const restore = async (r) => {
    if (!window.confirm(`Restore ${r.deleted_company || r.deleted_email} and everything that was removed with it?`)) return;
    try {
      const { data } = await api.post(`/admin/deletion-audit/${r.id}/restore`);
      toast.success(`Restored ${data.restored_user}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };

  const exportCsv = () => {
    const head = ["deleted_at", "deleted_email", "deleted_company", "deleted_role", "deleted_tier", "deleted_by_email", "method", "ip", "cascade"];
    const lines = [head.join(",")].concat(rows.map((r) => [
      r.deleted_at, r.deleted_email, r.deleted_company, r.deleted_role, r.deleted_tier, r.deleted_by_email, r.method, r.ip,
      Object.entries(r.cascade || {}).map(([k, v]) => `${v} ${k}`).join("; "),
    ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")));
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    a.download = `deletion-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4" data-testid="deletion-audit-panel">
      <div className="bg-white border border-[#E8ECE9] p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="font-serif text-xl text-[#11211A]">Deletion audit</div>
          <p className="text-xs text-[#7D8C85] mt-0.5">Every account removed by an admin and every listing deleted by a supplier — who, when, and what was removed. Listings can be restored for 30 days. <span data-testid="deletion-audit-total">{total} record{total === 1 ? "" : "s"}</span> total.</p>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email / company / admin" className="px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="deletion-audit-search" />
        <button type="button" onClick={load} className="btn-ghost text-xs" title="Refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
        <button type="button" onClick={exportCsv} disabled={!rows.length} className="btn-secondary text-xs disabled:opacity-50" data-testid="deletion-audit-export"><Download className="w-4 h-4" /> Export CSV</button>
      </div>

      <LoadErrorBanner error={loadError} onRetry={load} testId="deletion-audit-error" />
      <div className="bg-white border border-[#E8ECE9] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F0EFEA] text-[#4A5550] text-left text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 font-medium">Deleted</th>
              <th className="px-4 py-3 font-medium">Account</th>
              <th className="px-4 py-3 font-medium">Role / Tier</th>
              <th className="px-4 py-3 font-medium">Deleted by</th>
              <th className="px-4 py-3 font-medium">Method</th>
              <th className="px-4 py-3 font-medium">What was removed</th>
              <th className="px-4 py-3 font-medium">Undo</th>
            </tr>
          </thead>
          <tbody>
            {!loading && !loadError && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[#7D8C85]" data-testid="deletion-audit-empty">No deletions recorded yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#E8ECE9] align-top" data-testid={`deletion-audit-row-${r.id}`}>
                <td className="px-4 py-3 text-[#4A5550] whitespace-nowrap text-xs">{fmt(r.deleted_at)}</td>
                <td className="px-4 py-3">
                  {r.kind === "listing" && <div className="text-[9px] uppercase tracking-wider text-[#264839] font-semibold" data-testid={`deletion-audit-kind-${r.id}`}>Listing · {r.listing_title || r.listing_id}</div>}
                  <div className="text-[#11211A] break-all" data-testid={`deletion-audit-email-${r.id}`}>{r.deleted_email || "—"}</div>
                  {(r.deleted_company || r.deleted_name) && <div className="text-xs text-[#7D8C85]">{r.deleted_company || r.deleted_name}</div>}
                  {r.was_unclaimed && <span className="text-[9px] uppercase tracking-wider text-[#B35C4A]">was unclaimed</span>}
                </td>
                <td className="px-4 py-3 text-xs text-[#4A5550]">
                  <div className="capitalize">{r.deleted_role === "advertiser" ? "Supplier" : r.deleted_role || "—"}</div>
                  <div className="text-[#7D8C85] capitalize">{r.deleted_tier}</div>
                </td>
                <td className="px-4 py-3 text-xs text-[#4A5550]">
                  <div className="break-all">{r.deleted_by_email || r.deleted_by_admin_id}</div>
                  {r.ip && <div className="text-[#9AA6A0]">{r.ip}</div>}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${r.method === "bulk" ? "bg-[#FBECE8] text-[#B35C4A]" : "bg-[#E8ECE9] text-[#264839]"}`} data-testid={`deletion-audit-method-${r.id}`}>{r.method === "supplier_dashboard" ? "supplier (dashboard)" : r.method}</span>
                </td>
                <td className="px-4 py-3 text-xs text-[#4A5550]">
                  {r.cascade_total ? (
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(r.cascade || {}).map(([k, v]) => (
                        <span key={k} className="bg-[#F0EFEA] px-1.5 py-0.5">{v} {k.replace(/_/g, " ")}</span>
                      ))}
                    </div>
                  ) : <span className="text-[#9AA6A0]">Account only</span>}
                </td>
                <td className="px-4 py-3 text-xs whitespace-nowrap">
                  {r.restored_at ? (
                    <span className="text-[#264839]" data-testid={`deletion-audit-restored-${r.id}`}>Restored {fmt(r.restored_at)}</span>
                  ) : r.restorable_until && r.restorable_until > new Date().toISOString() ? (
                    <button type="button" onClick={() => restore(r)} className="btn-secondary text-xs inline-flex items-center gap-1" title={`Undo available until ${fmt(r.restorable_until)}`} data-testid={`deletion-audit-restore-${r.id}`}>
                      <Undo2 className="w-3.5 h-3.5" /> Restore
                    </button>
                  ) : <span className="text-[#9AA6A0]">{r.restorable_until ? "Window closed" : "—"}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
